import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";

interface LabInfo {
  lab_name: string;
  location: string;
  contact_no: string;
  email?: string;
}

interface PatientInfo {
  P_name: string;
  prefix: string;
  P_code: string;
  mobile: string;
  gender?: string;
  age?: number;
  age_type?: string;
  address?: string;
  email?: string;
}

interface BillData {
  id: number;
  billnum: string;
  bill_date: string;
  total: number;
  net_amount: number;
  cgst?: number;
  sgst?: number;
  home_visit?: number;
  paid_amount: number;
  balance: number;
  payment_method: string;
  reference_no?: string;
  ref_prefix: string;
  reference: string;
  bill_status: string;
  created_by?: string;
}

interface BillItem {
  item_name: string;
  amount: string | number;
  unit_price: string | number;
  quantity: string | number;
  uom: string;
}

// "single"/"group" test-category rollups returned by the backend —
// one row per selected profile/category instead of every sub-test.
interface CategoryDisplay {
  name: string;
  amount: string | number;
}

interface BillingA4Data {
  labInfo: LabInfo;
  patientInfo: PatientInfo | null;
  billData: BillData;
  testItems: BillItem[];
  singleDisplay?: CategoryDisplay[];
  groupDisplay?: CategoryDisplay[];
}

const BillingBillPdf: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<BillingA4Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (billId) {
      fetchReceipt(billId);
    } else {
      setError("No bill ID provided in URL");
      setLoading(false);
    }
  }, [billId]);

  const fetchReceipt = async (id: string) => {
    try {
      const res = await API.get(`/billing/receipt/a4/${id}`);
      if (res.data?.success) {
        setData(res.data.data);
      } else {
        setError("Receipt not found");
      }
    } catch (err) {
      console.error("BillingBillPdf fetchReceipt error:", err);
      setError("Failed to load receipt");
    } finally {
      setLoading(false);
    }
  };

  const n = (v: any) => parseFloat(String(v ?? 0)) || 0;

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60, fontFamily: "sans-serif" }}>
        <div className="spinner-border text-primary" />
        <p style={{ marginTop: 12 }}>Loading receipt…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: "red", fontFamily: "sans-serif" }}>
        <h3>⚠ Error</h3>
        <p>{error || "Receipt not found"}</p>
      </div>
    );
  }

  const { labInfo, patientInfo, billData, testItems, singleDisplay, groupDisplay } = data;

  // Flat directly-billed items (Consultation / Pharmacy / Lab / standalone tests)
  const flatDisplayItems = (testItems || []).map((t) => ({
    name: t.item_name,
    amount: n(t.amount),
  }));

  // "single" test-category selections — one rolled-up row per category
  const singleDisplayItems = (singleDisplay || []).map((c) => ({
    name: c.name,
    amount: n(c.amount),
  }));

  // "group" (profile) selections — one rolled-up row per category
  const groupDisplayItems = (groupDisplay || []).map((c) => ({
    name: c.name,
    amount: n(c.amount),
  }));

  // Merge everything into a single list for display — this is what fixes
  // "No items found" for bills that only contain single/group selections.
  const allItems = [...flatDisplayItems, ...singleDisplayItems, ...groupDisplayItems];

  const subTotal = allItems.reduce((s, i) => s + i.amount, 0);
  const netTotal = n(billData.net_amount);
  const paid     = n(billData.paid_amount);
  const balance  = n(billData.balance ?? netTotal - paid);
  const cgst     = n(billData.cgst);
  const sgst     = n(billData.sgst);

  const patientName = patientInfo
    ? `${patientInfo.prefix || ""}. ${patientInfo.P_name || ""}`.trim()
    : (billData as any).patient_name || "—";

  // FIXED: DD/MM/YYYY in Indian Standard Time. "en-GB" reliably renders
  // DD/MM/YYYY with "/" separators; timeZone locks it to IST regardless
  // of the server/browser's local timezone.
  const formattedBillDate = (() => {
    if (!billData.bill_date) return "—";
    const d = new Date(billData.bill_date);
    return isNaN(d.getTime())
      ? String(billData.bill_date)
      : d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
  })();

  return (
    <>
      <style>{`
        @page { size: A4; margin: 15mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; padding: 0 !important; margin: 0 !important; }
          .page-wrap { display: block !important; min-height: auto !important; padding: 0 !important; margin: 0 !important; background: white !important; }
          .a4 { width: 100% !important; min-height: auto !important; padding: 0 !important; box-shadow: none !important; margin: 0 !important; }
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          background: #e8e8e8;
          padding: 20px;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 12px;
          color: #222;
        }
        .page-wrap { display: flex; justify-content: center; align-items: flex-start; min-height: 100vh; }
        .a4 {
          background: white;
          width: 210mm;
          min-height: 297mm;
          padding: 15mm;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          position: relative;
        }

        /* ---- Lab Header: centered, stacked layout ---- */
        .lab-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          border-bottom: 3px double #2c3e50;
          padding-bottom: 12px;
          margin-bottom: 12px;
        }
        .lab-header .lab-name {
          font-size: 24px;
          font-weight: bold;
          color: #2c3e50;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .lab-header .lab-meta {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 4px 16px;
          margin-top: 4px;
        }
        .lab-header .lab-sub {
          font-size: 11px;
          color: #555;
        }
        .bill-title {
          margin-top: 10px;
          text-align: center;
          font-size: 18px;
          font-weight: bold;
          color: #c0392b;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .bill-title small {
          display: block;
          font-size: 11px;
          font-weight: normal;
          color: #555;
          text-transform: none;
          letter-spacing: normal;
          margin-top: 2px;
        }
        /* ---- End Lab Header ---- */

        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 14px;
          background: #f7f9fb;
          border: 1px solid #dde3e8;
          border-radius: 4px;
          padding: 10px 14px;
        }
        .info-grid .info-row   { display: flex; gap: 6px; margin-bottom: 3px; }
        .info-grid .info-label { color: #666; font-size: 11px; min-width: 90px; }
        .info-grid .info-value { font-weight: 600; font-size: 11px; }
        .section-title { font-size: 11px; font-weight: bold; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 11.5px; }
        thead tr { background: #2c3e50; color: white; }
        thead th { padding: 7px 10px; text-align: left; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
        thead th.text-right  { text-align: right; }
        thead th.text-center { text-align: center; }
        tbody tr:nth-child(even) { background: #f7f9fb; }
        tbody td { padding: 6px 10px; border-bottom: 1px solid #eee; vertical-align: top; }
        tbody td.text-right  { text-align: right; }
        tbody td.text-center { text-align: center; }
        tbody tr:last-child td { border-bottom: none; }
        .totals-wrap { display: flex; justify-content: flex-end; margin-bottom: 16px; }
        .totals-box  { width: 280px; border: 1px solid #dde3e8; border-radius: 4px; overflow: hidden; }
        .totals-row  { display: flex; justify-content: space-between; padding: 5px 12px; border-bottom: 1px solid #eee; font-size: 11.5px; }
        .totals-row:last-child   { border-bottom: none; }
        .totals-row.highlight    { background: #2c3e50; color: white; font-weight: bold; font-size: 13px; }
        .totals-row.paid-row     { color: #27ae60; font-weight: 600; }
        .totals-row.balance-row  { font-weight: 600; }
        .footer { border-top: 1px dashed #bbb; padding-top: 10px; display: flex; justify-content: space-between; font-size: 10px; color: #777; }
        .signature-box { text-align: center; border-top: 1px solid #333; padding-top: 4px; width: 140px; font-size: 10px; }
        .action-bar { display: flex; justify-content: center; gap: 12px; margin-top: 20px; }
        .btn-print { padding: 8px 28px; background: #2c3e50; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 13px; font-family: sans-serif; }
        .btn-print:hover { background: #34495e; }
        .btn-back  { padding: 8px 28px; background: #95a5a6; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 13px; font-family: sans-serif; }
        .btn-back:hover  { background: #7f8c8d; }
      `}</style>

      <div className="page-wrap">
        <div>
          <div className="a4">

            {/* Lab Header — centered: name, location, contact, then Invoice title */}
            <div className="lab-header">
              <div className="lab-name">{labInfo.lab_name}</div>
              <div className="lab-meta">
                {labInfo.location && <div className="lab-sub">📍 {labInfo.location}</div>}
                {labInfo.contact_no && <div className="lab-sub">📞 {labInfo.contact_no}</div>}
              </div>
              <div className="bill-title">
                Invoice
                <small>Bill Receipt</small>
              </div>
            </div>

            {/* Patient & Bill Info Grid */}
            <div className="info-grid">
              <div>
                <div className="section-title">Patient Details</div>
                <div className="info-row">
                  <span className="info-label">Name</span>
                  <span className="info-value">{patientName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Patient ID</span>
                  <span className="info-value">{patientInfo?.P_code || "—"}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Mobile</span>
                  <span className="info-value">{patientInfo?.mobile || "—"}</span>
                </div>
                {patientInfo?.age && (
                  <div className="info-row">
                    <span className="info-label">Age / Gender</span>
                    <span className="info-value">
                      {patientInfo.age} {patientInfo.age_type || "Yrs"}
                      {patientInfo.gender && ` / ${patientInfo.gender}`}
                    </span>
                  </div>
                )}
                {patientInfo?.address && (
                  <div className="info-row">
                    <span className="info-label">Address</span>
                    <span className="info-value">{patientInfo.address}</span>
                  </div>
                )}
              </div>
              <div>
                <div className="section-title">Bill Details</div>
                <div className="info-row">
                  <span className="info-label">Bill No.</span>
                  <span className="info-value" style={{ color: "#c0392b" }}>{billData.billnum}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Date</span>
                  <span className="info-value">{formattedBillDate}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Referred By</span>
                  <span className="info-value">{billData.ref_prefix} {billData.reference}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Payment</span>
                  <span className="info-value">{billData.payment_method}</span>
                </div>
                {billData.reference_no && (
                  <div className="info-row">
                    <span className="info-label">Ref No.</span>
                    <span className="info-value">{billData.reference_no}</span>
                  </div>
                )}
                {billData.created_by && (
                  <div className="info-row">
                    <span className="info-label">Created By</span>
                    <span className="info-value">{billData.created_by}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items Table */}
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Description</th>
                  <th className="text-right"  style={{ width: 120 }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {allItems.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center", color: "#999", padding: 20 }}>No items found</td>
                  </tr>
                ) : (
                  allItems.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{item.name}</td>
                      <td className="text-right">{item.amount.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Totals */}
            <div className="totals-wrap">
              <div className="totals-box">
                <div className="totals-row"><span>Sub Total</span><span>₹{subTotal.toFixed(2)}</span></div>
                {n(billData.home_visit) > 0 && (
                  <div className="totals-row"><span>Home Visit</span><span>₹{n(billData.home_visit).toFixed(2)}</span></div>
                )}
                {cgst > 0 && <div className="totals-row"><span>CGST</span><span>₹{cgst.toFixed(2)}</span></div>}
                {sgst > 0 && <div className="totals-row"><span>SGST</span><span>₹{sgst.toFixed(2)}</span></div>}
                <div className="totals-row highlight"><span>NET TOTAL</span><span>₹{netTotal.toFixed(2)}</span></div>
                {paid > 0 && (
                  <>
                    <div className="totals-row paid-row">
                      <span>Paid ({billData.payment_method})</span><span>₹{paid.toFixed(2)}</span>
                    </div>
                    <div className="totals-row balance-row" style={{ color: balance > 0 ? "#c0392b" : "#27ae60" }}>
                      <span>Balance Due</span><span>₹{balance.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="footer">
              <div>
                <div>Thank you for choosing {labInfo.lab_name}.</div>
                <div style={{ marginTop: 2 }}>This is a computer-generated receipt and does not require a physical signature.</div>
              </div>
              <div className="signature-box">Authorized Signatory</div>
            </div>

          </div>

          {/* Action Buttons */}
          <div className="action-bar no-print">
            <button className="btn-print" onClick={() => window.print()}>🖨 Print / Save PDF</button>
            <button className="btn-back"  onClick={() => navigate(-1)}>← Back</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BillingBillPdf;